import * as Yup from 'yup'
import React, { useState, useRef } from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material'
import { Formik, Form, Field } from 'formik'
import { useSelector } from 'react-redux'
import FormField from '../../shared/FormField'
import EquipmentListAPIs from '../../../utilities/api/equipment/AdminEquipmentList'
import CustomLocationDialog from './CustomLocation'
import CustomCategoryDialog from './CustomCategory'
import InventoryLocationAPIs from '../../../utilities/api/location/AdminLocationList'
import InventoryCategoryAPIs from '../../../utilities/api/category/AdminInventoryCategoryList'
import { toast } from 'react-toastify'
import { KeyboardArrowDown } from '@mui/icons-material'
import DocumentScannerOutlinedIcon from '@mui/icons-material/CropFree'

const availabilityList = ['Moderate', 'Very Low', 'Low', 'High', 'Always']
const statusList = ['Operational', 'Under Maintenance', 'Out of Service']

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Product type is required'),
  instances: Yup.number().required('Instances is required'),
  // upc: Yup.number().required('UPC is required'),
  upc: Yup.number(),
  description: Yup.string(),
  category: Yup.string(),
  location: Yup.string(),
  availability: Yup.string(),
  status: Yup.string(),
  supervised: Yup.boolean(),
  purchase_cost: Yup.string(),
  image: Yup.mixed(),
  purchase_date: Yup.date().nullable().typeError('Date has wrong format. Use YYYY-MM-DD.'),
  warranty_expiration: Yup.date().nullable().typeError('Date has wrong format. Use YYYY-MM-DD.')
})

const styledField = {
  maxWidth: '70%',
  width: '100%',
  padding: '0px 4px 8px 6px',
  '& .MuiFormLabel-root': {
    marginLeft: '4px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
    border: '1px solid #8C8C8C'
  }
}

const headerProps = { sx: { fontWeight: '400', color: '#666666' } }

const AddEquipmentDialog = ({
  open,
  onClose,
  update,
  setUpdate,
  flag,
  setFlag,
  setScannerOpen
}) => {
  const user = useSelector((state) => state.user.user)
  const token = user.access

  const [openCustomLocation, setOpenCustomLocation] = useState(false)
  const [customLocation, setCustomLocation] = useState('')
  const [openCustomCategory, setOpenCustomCategory] = useState(false)
  const [customCategory, setCustomCategory] = useState('')

  const [newImage, setNewImage] = useState(null)
  const fileInputRef = useRef(null)

  const category = useSelector((state) => state.category.category_list)
  const location = useSelector((state) => state.location.location_list)

  const categories = category.map((item) => item.name)
  const locations = location.map((item) => item.name)

  const handleConfirmLocation = () => {
    setOpenCustomLocation(false)
    const response = InventoryLocationAPIs.LocationListPost(customLocation)
    response
      .then((result) => {
        console.log(result)
        toast.success('Location added successfully')
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        console.log('error', err)
        const errorMessage =
          err.response?.data?.message || 'Failed to add location. Please try again.'
        toast.error(`Error: ${errorMessage}`)
      })
  }

  const handleConfirmCategory = () => {
    setOpenCustomCategory(false)
    const response = InventoryCategoryAPIs.CategoryListPost(customCategory)
    response
      .then((result) => {
        console.log(result)
        toast.success('Category added successfully')
        setFlag((prev) => !prev)
      })
      .catch((err) => {
        console.log('error', err)
        const errorMessage =
          err.response?.data?.message || 'Failed to add category. Please try again.'
        toast.error(`Error: ${errorMessage}`)
      })
  }

  const handleFormClose = () => {
    setNewImage(null) // Reset the image
    onClose() // Call the original onClose function
    toast.info('Form discarded')
  }

  return (
    <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add New Equipment</Typography>

          {/* Barcode Scanner Icon Button */}
          <Tooltip title="Coming soon">
            <IconButton
              sx={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid #E6E6E6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              // onClick={() => setScannerOpen(true)} // Open the scanner dialog
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 400, fontSize: '16px', color: '#5C5C5C', mr: '8px' }}
              >
                Scan
              </Typography>
              <DocumentScannerOutlinedIcon sx={{ fontSize: '30px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <Formik
        initialValues={{
          name: '',
          upc: '',
          manufacturer: '',
          description: ' ',
          location: '',
          instances: '',
          availability: '',
          status: '',
          supervised: false,
          purchase_cost: '',
          purchase_date: '',
          warranty_expiration: '',
          image: '',
          category: ''
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          console.log(values)
          const formData = new FormData()

          // Append all values (matching AddMachine pattern)
          Object.entries(values).forEach(([key, value]) => {
            formData.append(key, value)
          })

          // Add image if exists
          if (values.image) {
            formData.append('image', values.image)
          }

          // Add availability as index (0-4)
          formData.append('availability1', availabilityList.indexOf(values.availability).toString())

          // Add status as index (0-2)
          formData.append('status1', statusList.indexOf(values.status).toString())

          const response = EquipmentListAPIs.AddInventory(formData, token)
          response
            .then((result) => {
              console.log(result)
              toast.success('Equipment added successfully')
              setUpdate((prev) => !prev)
              setNewImage(null) // Reset the image on successful submit
              resetForm()
              onClose()
            })
            .catch((error) => {
              console.log(error)
              if (error.response && error.response.data) {
                const errorMessages = Object.values(error.response.data).flat().join(' ')
                toast.error(`Failed to add equipment: ${errorMessages}`)
              } else {
                toast.error('Failed to add equipment: An unexpected error occurred.')
              }
            })
        }}
      >
        {({ handleSubmit, values, touched, errors, handleChange, setFieldValue }) => {
          // Define handleFileChange within the Formik context
          const handleFileChange = (event) => {
            const file = event.currentTarget.files[0]

            if (file) {
              // Check if file size exceeds 1MB (1MB = 1,048,576 bytes)
              const fileSizeLimit = 1048576 // 1MB in bytes
              if (file.size > fileSizeLimit) {
                toast.error('File size should not exceed 1MB')
                return // Do not proceed with file upload
              }

              // Check if the file is an image
              if (!file.type.startsWith('image/')) {
                toast.error('Only image files are allowed')
                return
              }

              // If both validations pass, set the image and display a preview
              setFieldValue('image', file)
              setNewImage(URL.createObjectURL(file))
            }
          }

          return (
            <Form onSubmit={handleSubmit} encType="multipart/form-data">
              <DialogContent>
                <Grid container spacing={10}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      name="name"
                      label="Name"
                      heading="Name*"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="upc"
                      label="Enter UPC"
                      fieldType="textfield"
                      heading="UPC"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="instances"
                      label="Enter Instances"
                      fieldType="textfield"
                      heading="Instances*"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="manufacturer"
                      label="Enter Manufacturer Name"
                      fieldType="textfield"
                      heading="Manufacturer"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="purchase_cost"
                      label="Enter Purchase Cost"
                      fieldType="textfield"
                      heading="Purchase Cost"
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <Box>
                      <FormField
                        name="location"
                        label="Select Location"
                        fieldType="dropdown"
                        heading="Location"
                        options={locations}
                        sx={styledField}
                        SelectProps={{
                          IconComponent: KeyboardArrowDown
                        }}
                        addOption
                        handleAddOption={() => setOpenCustomLocation(true)}
                        headerProps={headerProps}
                      />
                    </Box>
                    <CustomLocationDialog
                      openCustomLocation={openCustomLocation}
                      setCustomLocation={setCustomLocation}
                      onClose={() => setOpenCustomLocation(false)}
                      onConfirm={() => {
                        setFieldValue('location', customLocation)
                        handleConfirmLocation()
                      }}
                    />
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: '400', padding: '5px', color: '#666666' }}
                      >
                        Equipment Image
                      </Typography>

                      {newImage ? (
                        <img
                          src={newImage}
                          alt="New Upload"
                          style={{ width: '200px', height: '200px' }}
                        />
                      ) : null}

                      <Button
                        variant="contained"
                        onClick={() => fileInputRef.current.click()}
                        sx={{
                          height: newImage ? '40px' : '100px', // Dynamic height based on image upload
                          width: '150px',
                          padding: newImage ? '8px 12px' : '12px 16px', // Adjusted padding for smaller button when image is uploaded
                          margin: 1,
                          background: newImage ? '#FFE6E6' : '#E6F2FF', // Optional: change button color if image is uploaded
                          color: '#3F3F3F',
                          border: newImage ? '1px solid #FF6666' : '1px solid #66B0FF', // Optional: change border color if image is uploaded
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column' // Stack icon above the text
                        }}
                      >
                        <Typography
                          sx={{ fontSize: newImage ? 14 : 24, p: 0, m: 0, mt: newImage ? 0 : -1 }}
                        >
                          {newImage ? 'Change Image' : '+'}
                        </Typography>
                        {!newImage && (
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                            Upload File
                          </Typography>
                        )}
                      </Button>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />

                      {touched.image && errors.image && (
                        <FormHelperText error>{errors.image}</FormHelperText>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <FormField
                        name="category"
                        label="Select Category"
                        fieldType="dropdown"
                        heading="Category"
                        options={categories}
                        sx={styledField}
                        SelectProps={{
                          IconComponent: KeyboardArrowDown
                        }}
                        addOption
                        handleAddOption={() => setOpenCustomCategory(true)}
                        headerProps={headerProps}
                      />
                    </Box>
                    <CustomCategoryDialog
                      openCustomCategory={openCustomCategory}
                      setCustomCategory={setCustomCategory}
                      onClose={() => setOpenCustomCategory(false)}
                      onConfirm={() => {
                        setFieldValue('category', customCategory)
                        handleConfirmCategory()
                      }}
                    />
                    <FormField
                      name="availability"
                      fieldType="dropdown"
                      heading="Availability"
                      label="Select Availability"
                      options={availabilityList}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="status"
                      fieldType="dropdown"
                      heading="Status"
                      label="Select Status"
                      options={statusList}
                      sx={styledField}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown
                      }}
                      headerProps={headerProps}
                    />
                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="supervised"
                          color="primary"
                          checked={values.supervised}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.25rem', color: '#666666' }}>
                          Supervised
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                    {touched.supervised && errors.supervised && (
                      <FormHelperText error>{errors.supervised}</FormHelperText>
                    )}
                    <FormField
                      name="purchase_date"
                      label="Purchase Date"
                      fieldType="date"
                      heading="Purchase Date"
                      sx={styledField}
                      headerProps={headerProps}
                      format="YYYY-MM-DD"
                    />
                    <FormField
                      name="warranty_expiration"
                      label="Warranty Expiration"
                      fieldType="date"
                      heading="Warranty Expiration"
                      sx={styledField}
                      headerProps={headerProps}
                      format="YYYY-MM-DD"
                    />
                  </Grid>
                </Grid>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 400, color: '#666666', mt: 4 }}>
                  Description
                </Typography>
                <Field
                  as={TextField}
                  name="description"
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={values.description}
                  onChange={handleChange}
                  sx={{ mt: 2, mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleFormClose}
                  color="primary"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    borderWidth: '1px',
                    borderColor: 'action.active',
                    '&:hover': {
                      borderWidth: '1px',
                      borderColor: 'action.active'
                    },
                    mr: 1
                  }}
                >
                  Discard
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'common.white',
                    backgroundColor: 'primary.main',
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: 'primary.dark'
                    }
                  }}
                >
                  Add Eqipment
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default AddEquipmentDialog
